const CRLF = "\r\n";

const FROM_HEADER = "HVM Dashboard <noreply@humanvsmachine.co>";

const isAscii = (value: string): boolean => /^[\x20-\x7E]*$/.test(value);

/**
 * btoa() works on binary strings, so the UTF-8 bytes have to be walked one by
 * one: spreading a Uint8Array needs downlevelIteration, which this project
 * (target: es5) does not enable.
 */
const toBase64Utf8 = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
};

/** RFC 2047 encoded-word, needed as soon as a header value is not plain ASCII. */
const encodeHeaderValue = (value: string): string =>
  isAscii(value) ? value : `=?utf-8?B?${toBase64Utf8(value)}?=`;

/** RFC 2045 caps base64 bodies at 76 characters per line. */
const wrapBase64 = (value: string): string =>
  (value.match(/.{1,76}/g) || []).join(CRLF);

const toRfc2822Date = (date: Date): string =>
  date.toUTCString().replace(/GMT$/, "+0000");

/**
 * Wraps the rendered signature in an RFC 5322 message so it can be opened in
 * Apple Mail, then copied from the message window into the signature editor.
 *
 * The body is always base64: the EJS templates are minified at build time
 * (htmlmin), so a quoted-printable or 8bit body would blow past the 998
 * characters per line limit.
 */
const buildEmlFile = (html: string, name: string): string => {
  const headers = [
    `From: ${FROM_HEADER}`,
    `Subject: ${encodeHeaderValue(`Firma email - ${name}`)}`,
    `Date: ${toRfc2822Date(new Date())}`,
    "MIME-Version: 1.0",
    "Content-Type: text/html; charset=utf-8",
    "Content-Transfer-Encoding: base64",
  ];

  const body = wrapBase64(toBase64Utf8(html));

  return headers.join(CRLF) + CRLF + CRLF + body + CRLF;
};

/** "Mario Rossì" -> "Firma_Mario_Rossi.eml" */
const buildEmlFileName = (name: string): string => {
  const slug = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "");

  return `Firma_${slug || "email_signature"}.eml`;
};

export { buildEmlFile, buildEmlFileName };
